import type { BannerBlock as BannerBlockProps } from '@/payload-types'

import type { LexicalRichTextBody } from '@/components/RichText/blockConvertersTypes'
import { cn } from '@/utilities/ui'
import React from 'react'

type BannerAriaStrings = {
  bannerInfo: string
  bannerError: string
  bannerSuccess: string
  bannerWarning: string
  bannerDefault: string
}

type Props = {
  className?: string
  lexicalRichText: LexicalRichTextBody
  /** Strings a11y traduzidas (namespace «Blocks»); injetadas pelo conversor de RichText. */
  ariaStrings?: BannerAriaStrings
} & BannerBlockProps

/** Defaults pt — usados quando o módulo de idiomas está inativo e o conversor não injeta strings. */
const BANNER_ARIA_DEFAULTS: BannerAriaStrings = {
  bannerInfo: 'Informação ou dica',
  bannerError: 'Aviso de erro',
  bannerSuccess: 'Mensagem de sucesso',
  bannerWarning: 'Aviso ou observação',
  bannerDefault: 'Destaque editorial',
}

export const BannerBlock: React.FC<Props> = ({ className, content, style, lexicalRichText: Lexical, ariaStrings }) => {
  const s = ariaStrings ?? BANNER_ARIA_DEFAULTS
  const styleAria: Record<NonNullable<BannerBlockProps['style']>, string> = {
    info: s.bannerInfo,
    error: s.bannerError,
    success: s.bannerSuccess,
    warning: s.bannerWarning,
  }
  const aria = style ? styleAria[style] : s.bannerDefault

  return (
    <aside
      aria-label={aria}
      className={cn('mx-auto my-8 w-full', className)}
      role="note"
    >
      <div
        className={cn('flex items-center rounded border px-6 py-3', {
          'border-border bg-card': style === 'info',
          'border-error bg-error/30': style === 'error',
          'border-success bg-success/30': style === 'success',
          'border-warning bg-warning/30': style === 'warning',
        })}
      >
        <Lexical data={content} enableGutter={false} enableProse={false} />
      </div>
    </aside>
  )
}
